import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { FilterMatchMode } from 'primereact/api';
import { Card } from 'primereact/card';
import { jobsApi } from '../api/jobs';
import { clientsApi } from '../api/clients';
import { BackgroundJob, Client, JobStatus, JobType } from '../types';
import { useDataTableState, usePersistentState } from '../hooks';

const statusOptions = [
  { label: 'All Statuses', value: null },
  { label: 'Pending', value: 'pending' },
  { label: 'Running', value: 'running' },
  { label: 'Completed', value: 'completed' },
  { label: 'Failed', value: 'failed' },
];

const jobTypeLabels: Record<JobType, string> = {
  identify_customers: 'Identify Customers',
  parse_bank_statement: 'Parse Bank Statement',
  parse_deposits: 'Parse Deposits',
};

export const JobHistory: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [jobs, setJobs] = useState<BackgroundJob[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<BackgroundJob | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const [statusFilter, setStatusFilter] = usePersistentState<JobStatus | null>(
    'filters:jobHistory:status',
    null
  );
  const [clientFilter, setClientFilter] = usePersistentState<string | null>(
    'filters:jobHistory:client',
    null
  );

  const { sortField, sortOrder, handleSort, filters } = useDataTableState({
    storageKey: 'jobHistory',
    defaultSort: { field: 'created_at', order: -1 },
    defaultFilters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    },
  });

  useEffect(() => {
    loadData();
  }, [statusFilter, clientFilter]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [jobsResponse, clientsResponse] = await Promise.all([
        jobsApi.listJobs({
          status: statusFilter || undefined,
          client_id: clientFilter || undefined,
          limit: 100,
        }),
        clientsApi.listClients(),
      ]);
      setJobs(jobsResponse.items);
      setClients(clientsResponse);
    } catch (error: unknown) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail:
          (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          'Failed to load job history',
        life: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getClientName = (clientId: string | null): string => {
    if (!clientId) return '-';
    const client = clients.find((c) => c.id === clientId);
    return client?.name || 'Unknown';
  };

  const clientOptions = [
    { label: 'All Clients', value: null },
    ...clients.map((c) => ({ label: c.name, value: c.id })),
  ];

  const statusBodyTemplate = (rowData: BackgroundJob) => {
    const severityMap: Record<JobStatus, 'success' | 'info' | 'warning' | 'danger' | 'secondary'> = {
      pending: 'warning',
      running: 'info',
      completed: 'success',
      failed: 'danger',
    };
    return <Tag value={rowData.status} severity={severityMap[rowData.status]} />;
  };

  const jobTypeBodyTemplate = (rowData: BackgroundJob) => {
    return jobTypeLabels[rowData.job_type] || rowData.job_type;
  };

  const clientBodyTemplate = (rowData: BackgroundJob) => {
    return getClientName(rowData.client_id);
  };

  const dateBodyTemplate = (rowData: BackgroundJob, field: 'created_at' | 'completed_at') => {
    const value = rowData[field];
    if (!value) return '-';
    return new Date(value).toLocaleString();
  };

  const progressBodyTemplate = (rowData: BackgroundJob) => {
    if (rowData.status === 'pending') {
      return <span className="status-text-muted">Waiting...</span>;
    }
    if (rowData.status === 'running') {
      const percent =
        rowData.progress_total > 0
          ? Math.round((rowData.progress_current / rowData.progress_total) * 100)
          : 0;
      return (
        <div className="progress-cell">
          <ProgressBar value={percent} showValue />
          {rowData.progress_message && (
            <small className="status-text-muted">{rowData.progress_message}</small>
          )}
        </div>
      );
    }
    if (rowData.status === 'completed') {
      return <span className="status-text-success">Done</span>;
    }
    if (rowData.status === 'failed') {
      return (
        <Button
          label="View Error"
          link
          onClick={() => {
            setSelectedJob(rowData);
            setShowDetailsDialog(true);
          }}
        />
      );
    }
    return '-';
  };

  const actionBodyTemplate = (rowData: BackgroundJob) => {
    return (
      <div className="row-actions">
        <Button
          icon="ms-out ms-out-info"
          link
          tooltip="View Details"
          tooltipOptions={{ position: 'bottom' }}
          onClick={() => {
            setSelectedJob(rowData);
            setShowDetailsDialog(true);
          }}
        />
      </div>
    );
  };

  const formatDuration = (job: BackgroundJob): string => {
    if (!job.started_at) return '-';
    const start = new Date(job.started_at);
    const end = job.completed_at ? new Date(job.completed_at) : new Date();
    const durationMs = end.getTime() - start.getTime();
    const seconds = Math.floor(durationMs / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <>
      <Toast ref={toast} />
      <div className="page-container">

        <div className="controls-panel">
          <Dropdown
            value={statusFilter}
            options={statusOptions}
            onChange={(e) => setStatusFilter(e.value)}
            placeholder="Filter by Status"
          />
          <Dropdown
            value={clientFilter}
            options={clientOptions}
            onChange={(e) => setClientFilter(e.value)}
            placeholder="Filter by Client"
            filter
            showClear
          />
          <Button icon="ms-out ms-out-sync" tooltip="Refresh" onClick={loadData} outlined />
        </div>

        <DataTable
          value={jobs}
          dataKey="id"
          loading={isLoading}
          size="small"
          stripedRows
          emptyMessage="No jobs found"
          scrollable
          scrollHeight="flex"
          paginator
          rows={25}
          rowsPerPageOptions={[25, 50, 100]}
          paginatorTemplate={{
            layout:
              'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
            CurrentPageReport: (options) => (
              <span className="paginator-current">
                Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> jobs
              </span>
            ),
          }}
          filters={filters}
          sortField={sortField ?? undefined}
          sortOrder={sortOrder ?? undefined}
          onSort={handleSort}
        >
          <Column field="job_type" header="Job Type" body={jobTypeBodyTemplate} sortable />
          <Column field="status" header="Status" body={statusBodyTemplate} sortable />
          <Column field="client_id" header="Client" body={clientBodyTemplate} />
          <Column header="Progress" body={progressBodyTemplate} />
          <Column
            field="created_at"
            header="Created"
            body={(rowData) => dateBodyTemplate(rowData, 'created_at')}
            sortable
          />
          <Column
            field="completed_at"
            header="Completed"
            body={(rowData) => dateBodyTemplate(rowData, 'completed_at')}
            sortable
          />
          <Column body={actionBodyTemplate} />
        </DataTable>
      </div>

      <Dialog
        header="Job Details"
        visible={showDetailsDialog}
        onHide={() => {
          setShowDetailsDialog(false);
          setSelectedJob(null);
        }}
      >
        {selectedJob && (
          <table className="info-table">
            <tbody>
              <tr>
                <th>Job ID</th>
                <td>{selectedJob.id}</td>
              </tr>
              <tr>
                <th>Type</th>
                <td>{jobTypeLabels[selectedJob.job_type]}</td>
              </tr>
              <tr>
                <th>Status</th>
                <td>{statusBodyTemplate(selectedJob)}</td>
              </tr>
              <tr>
                <th>Client</th>
                <td>{getClientName(selectedJob.client_id)}</td>
              </tr>
              <tr>
                <th>Created</th>
                <td>{new Date(selectedJob.created_at).toLocaleString()}</td>
              </tr>
              {selectedJob.started_at && (
                <tr>
                  <th>Started</th>
                  <td>{new Date(selectedJob.started_at).toLocaleString()}</td>
                </tr>
              )}
              {selectedJob.completed_at && (
                <tr>
                  <th>Completed</th>
                  <td>{new Date(selectedJob.completed_at).toLocaleString()}</td>
                </tr>
              )}
              <tr>
                <th>Duration</th>
                <td>{formatDuration(selectedJob)}</td>
              </tr>
              {selectedJob.progress_total > 0 && (
                <tr>
                  <th>Progress</th>
                  <td>
                    {selectedJob.progress_current} / {selectedJob.progress_total}
                  </td>
                </tr>
              )}
              {selectedJob.status === 'completed' && selectedJob.result && (
                <>
                  {selectedJob.job_type === 'identify_customers' && (
                    <>
                      <tr>
                        <th colSpan={2} style={{ paddingTop: '1rem' }}>
                          <strong>Results</strong>
                        </th>
                      </tr>
                      <tr>
                        <th>Checks Processed</th>
                        <td>{(selectedJob.result as Record<string, number>).checks_processed ?? '-'}</td>
                      </tr>
                      <tr>
                        <th>Deposits Processed</th>
                        <td>{(selectedJob.result as Record<string, number>).deposits_processed ?? '-'}</td>
                      </tr>
                      <tr>
                        <th>Customers Auto-Linked</th>
                        <td>{(selectedJob.result as Record<string, number>).customers_auto_linked ?? '-'}</td>
                      </tr>
                      <tr>
                        <th>Vendors Auto-Linked</th>
                        <td>{(selectedJob.result as Record<string, number>).vendors_auto_linked ?? '-'}</td>
                      </tr>
                      <tr>
                        <th>Customer Suggestions Created</th>
                        <td>{(selectedJob.result as Record<string, number>).customer_inferences_created ?? '-'}</td>
                      </tr>
                      <tr>
                        <th>Vendor Suggestions Created</th>
                        <td>{(selectedJob.result as Record<string, number>).vendor_inferences_created ?? '-'}</td>
                      </tr>
                    </>
                  )}
                </>
              )}
              {selectedJob.error && (
                <tr className="error-row">
                  <th>Error</th>
                  <td>
                    <pre>{selectedJob.error}</pre>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Dialog>
    </>
  );
};

export default JobHistory;
